/**
 * セクション内に挿入するカスタムフィールド入力。
 *
 * - グローバル定義 (UnitCustomField) を section_key でフィルタしてレンダー
 * - 末尾の「+項目を追加」入力で **pending fields** に push (API は叩かない)。
 *   pending は form values の `__pending_custom_fields` に持つ (UnitTabForm 側で
 *   submit 時に master upsert → custom_fields にマージ)。
 *
 * 入力欄は **テキスト固定**。
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  CustomTextInput,
  CustomFormCreateInput,
  CustomSectionTitle,
} from "@ippon/shared-components";
import { useDispatch, useSelector } from "react-redux";
import type { UseFormReturn } from "react-hook-form";
import type { AppDispatch, RootState } from "@/store";
import { fetchUnitCustomFields } from "@/store/unitCustomFieldSlice";
import type {
  CustomFieldValue,
  UnitCustomFieldSection,
} from "@ippon/shared-types";
import type { PendingCustomField, UnitFormValues } from "../UnitTabForm";

interface Props {
  form: UseFormReturn<UnitFormValues>;
  sectionKey: UnitCustomFieldSection;
  labelWidth?: string;
  inputWidth?: string;
  /** カスタム項目群の小見出し (省略時は出さない)。例: 「カスタムフォーム」 */
  title?: string;
}

const UnitSectionCustomFields: React.FC<Props> = ({
  form,
  sectionKey,
  labelWidth,
  inputWidth,
  title,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { data: definitions, loading, loaded } = useSelector(
    (s: RootState) => s.unitCustomFields.list,
  );

  // 「+項目を追加」UI 状態 (ラベル入力欄のみコンポーネント内 state)
  const [pendingNewLabel, setPendingNewLabel] = useState("");

  useEffect(() => {
    if (!loaded && !loading) {
      void dispatch(fetchUnitCustomFields());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applicable = useMemo(
    () =>
      definitions
        .filter((d) => (d.section_key ?? "basic") === sectionKey)
        .sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999)),
    [definitions, sectionKey],
  );

  const values: CustomFieldValue[] = form.watch("custom_fields") || [];
  const valueMap = new Map(values.map((v) => [v.field_id, v.value]));

  const allPending: PendingCustomField[] =
    form.watch("__pending_custom_fields") || [];
  const pendingInSection = allPending.filter(
    (p) => p.section_key === sectionKey,
  );

  // ── グローバル定義の RHF への動的登録 + 値同期 ────────
  // CustomTextInput が必須バリデーション (空時のエラーメッセージ) を表示するには
  // 親 form (UnitTabForm) の register / errors / watch / setValue を介する必要がある。
  // フィールド名は __cf_<field_id> で衝突回避。
  const globalKey = (fid: string) => `__cf_${fid}`;
  const normalizeTextValue = (value: CustomFieldValue["value"]) => {
    if (value == null) return "";
    if (Array.isArray(value)) return value.join(",");
    return String(value);
  };

  // グローバル定義: register に required ルールを動的登録 + 初期値同期
  useEffect(() => {
    applicable.forEach((def) => {
      const key = globalKey(def.field_id);
      form.register(key as any, {
        required: def.required ? `${def.label}は必須です` : false,
      });
      const v = normalizeTextValue(valueMap.get(def.field_id) ?? "");
      if (form.getValues(key as any) !== v) {
        form.setValue(key as any, v, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicable.map((d) => `${d.field_id}:${d.required ? 1 : 0}`).join("|")]);

  // form の値変化 → custom_fields[] への書き戻し
  useEffect(() => {
    const sub = form.watch((values, info) => {
      const name = info?.name as string | undefined;
      if (!name) return;
      if (name.startsWith("__cf_")) {
        const fid = name.slice("__cf_".length);
        const v = String(values[name as keyof typeof values] ?? "");
        const cur = (form.getValues("custom_fields") || []) as CustomFieldValue[];
        if (
          normalizeTextValue(cur.find((x) => x.field_id === fid)?.value ?? "") !==
          v
        ) {
          const next = cur.filter((x) => x.field_id !== fid);
          next.push({ field_id: fid, value: v });
          form.setValue("custom_fields", next, { shouldDirty: true });
        }
      }
    });
    return () => sub.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── pending fields 操作 ─────────────────────
  const updatePendingValue = (tempId: string, value: string) => {
    const next = allPending.map((p) =>
      p.temp_id === tempId ? { ...p, value } : p,
    );
    form.setValue("__pending_custom_fields", next, { shouldDirty: true });
  };

  const removePending = (tempId: string) => {
    form.setValue(
      "__pending_custom_fields",
      allPending.filter((p) => p.temp_id !== tempId),
      { shouldDirty: true },
    );
  };

  const handleAdd = () => {
    const label = pendingNewLabel.trim();
    if (!label) return;
    const newItem: PendingCustomField = {
      temp_id: `pending_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      label,
      section_key: sectionKey,
      value: "",
    };
    form.setValue(
      "__pending_custom_fields",
      [...allPending, newItem],
      { shouldDirty: true },
    );
    setPendingNewLabel("");
  };

  const hasContent = applicable.length > 0 || pendingInSection.length > 0;

  return (
    <Box sx={{ mt: title ? 3 : 0 }}>
      {title && (
        <Box sx={{ mb: 2 }}>
          <CustomSectionTitle title={title} size="small" />
        </Box>
      )}
      <Stack spacing={0} sx={{ mt: hasContent ? 0.5 : 0 }}>
        {/* グローバル定義 (RHF に動的登録、必須は parent form のバリデーションで検出) */}
        {applicable.map((def) => (
          <CustomTextInput
            key={def.field_id}
            label={def.label}
            name={globalKey(def.field_id)}
            register={form.register}
            errors={form.formState.errors}
            isRequired={!!def.required}
            labelWidth={labelWidth}
            inputWidth={inputWidth}
          />
        ))}

        {/* pending 項目 (未保存): submit 時に master upsert される */}
        {pendingInSection.map((p) => (
          <Box key={p.temp_id} sx={{ mb: 2 }}>
            <Typography
              sx={{
                fontSize: "12px !important",
                fontWeight: 700,
                paddingBottom: "4px",
                marginBottom: "1px",
                textAlign: "left",
              }}
            >
              {p.label}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Box sx={{ width: inputWidth || "600px", maxWidth: "100%" }}>
                <TextField
                  size="small"
                  fullWidth
                  value={p.value}
                  onChange={(e) => updatePendingValue(p.temp_id, e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "white",
                      borderRadius: "8px",
                      height: "34px",
                      fontSize: 12,
                    },
                  }}
                />
              </Box>
              <IconButton
                size="small"
                onClick={() => removePending(p.temp_id)}
                title="この項目を削除"
                sx={{ color: "#999" }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Stack>
          </Box>
        ))}
      </Stack>

      {/* 「+項目を追加」入力 (添付書類と同じパターン) */}
      <Box sx={{ mt: hasContent ? 1 : 0.5, width: inputWidth, maxWidth: "100%" }}>
        <CustomFormCreateInput
          value={pendingNewLabel}
          onChange={setPendingNewLabel}
          onAdd={handleAdd}
        />
      </Box>
    </Box>
  );
};

export default UnitSectionCustomFields;
